import type { CcmOnlyPatientState, GetCcmOnlyPatientStateResponse } from '@/shared/api/graphql/clinicalCare';
import { GET_CCM_ONLY_PATIENT_STATE } from '@/shared/api/graphql/clinicalCare';
import { useFeatureFlag } from '@/shared/lib/hooks/useFeatureFlag';
import useShowMedicalServices from '@/shared/lib/hooks/useShowMedicalServices';
import { MedicalServices } from '@/shared/lib/types/admin';
import { FeatureFlag } from '@/shared/lib/types/relyingParty';
import { useQuery } from '@apollo/client';

/** Whether the current pharmacy can enroll patients in CCM without RPM. Off unless turned on. */
export const useCcmOnlyEnrollment = () => useFeatureFlag(FeatureFlag.CCM_ONLY_ENROLLMENT);

export const CCM_ONLY_PATIENT_STATE_OFF: CcmOnlyPatientState = {
  ccmOnlyPath: false,
  ccmOnlyPathExempt: false,
  canStartNewInitialVisit: false,
  initialVisitSignedOffAt: null
};

type CcmOnlyPatient = { truentityId?: string | null } | null | undefined;

/** The backend's CCM-only state for this patient; all off when the setting is off, undefined while it loads. */
export const useCcmOnlyPatientState = (patient: CcmOnlyPatient): CcmOnlyPatientState | undefined => {
  const ccmOnlyEnrollmentEnabled = useCcmOnlyEnrollment();
  const truentityId = patient?.truentityId;
  const { data } = useQuery<GetCcmOnlyPatientStateResponse>(GET_CCM_ONLY_PATIENT_STATE, {
    variables: { truentityId },
    skip: !ccmOnlyEnrollmentEnabled || !truentityId,
    fetchPolicy: 'cache-and-network'
  });

  if (!ccmOnlyEnrollmentEnabled) return CCM_ONLY_PATIENT_STATE_OFF;
  return data?.ccmOnlyAccountState;
};

/** Whether this patient is on the CCM-only path: always false with the setting off, undefined until the backend answers. */
export const useIsCcmOnlyPath = (patient: CcmOnlyPatient): boolean | undefined => useCcmOnlyPatientState(patient)?.ccmOnlyPath;

/**
 * Whether the patient's Care Management pages (Initial Visit, Activities, Encounters...) apply.
 * Today that means an RPM pharmacy and an RPM status. With CCM-only enrollment on, a CCM status at a
 * CCM pharmacy also counts, so a pharmacy that offers CCM without RPM (D6) can reach them.
 */
export const useIsCareManagementViewable = (patient: { rpmStatus?: string | null; ccmStatus?: string | null } | null | undefined) => {
  const isRPMViewable = useShowMedicalServices(MedicalServices.RPM);
  const isCCMViewable = useShowMedicalServices(MedicalServices.CCM);
  const ccmOnlyEnrollmentEnabled = useCcmOnlyEnrollment();

  const viaRpm = isRPMViewable && patient?.rpmStatus !== null;
  const viaCcmOnly = ccmOnlyEnrollmentEnabled && isCCMViewable && patient?.ccmStatus != null;
  return viaRpm || viaCcmOnly;
};
